import { recursiveReadonly } from "@p0security/util/types/util";
import { capitalize } from "lodash-es";
import pluralize from "pluralize";

import type {
  AssessmentNodes,
  Identity,
  IdentityType,
  TargetNodeType,
} from "../types/assessment/data/index.ts";
import {
  MetaTargetNodeTypes,
  TargetNodeTypes,
} from "../types/assessment/data/index.ts";
import type { MonitorPriority } from "../types/assessment/monitor.ts";
import { assertNever } from "../types/index.ts";
import { providerLabels } from "./render.ts";

export const DEFAULT_AUTHN_LOOKBACK_DAYS = 40;

export const USAGE_LOOKBACK_DAYS = 90;

export const MAX_AWS_AUTHN_LOOKBACK_DAYS = 400;

export const ASSESSMENT_TARGET_REGEX = /^[\w\-_.\s]+$/;

export const AWS_RESOURCE_ARN = /arn:.*?:([^:]+):.*?:.*?:((.+?)[/:])?(.*)/;

export const SharedNodeTitles = {
  condition: "Condition",
  credential: "Credential",
  consumer: "Consumer",
  identity: "Identity",
  lateral: "Lateral movement",
  resource: "Resource",
  risk: "Risk",
  usage: "Permissions",
} as const;

export const ProviderNodeTitles = recursiveReadonly({
  all: {
    grant: "Entitlement",
    permissionSet: "AWS permission set",
    privileges: "Privileges",
    privilegeSet: "Role / policy",
  },
  aws: {
    grant: "AWS IAM policy assignment",
    permissionSet: "AWS permission set",
    privileges: "Actions",
    privilegeSet: "AWS IAM policy",
  },
  azure: {
    grant: "Azure role assignment",
    privileges: "Permissions",
    privilegeSet: "Azure role",
  },
  "azure-ad": {
    grant: "Entra role assignment",
    privileges: "Permissions",
    privilegeSet: "Entra role",
  },
  "entra-id": {
    grant: "Entra role assignment",
    privileges: "Permissions",
    privilegeSet: "Entra role",
  },
  custom: {
    grant: "Custom entitlement",
    privileges: "Permissions",
    privilegeSet: "Custom role",
  },
  gcp: {
    grant: "GCP role binding",
    privileges: "Permissions",
    privilegeSet: "GCP role",
  },
  k8s: {
    grant: "Kubernetes role assignment",
    privileges: "Verbs",
    privilegeSet: "Kubernetes role",
  },
  okta: {
    grant: "Okta role assignment",
    privileges: "Permissions",
    privilegeSet: "Administrator role",
  },
  workspace: {
    grant: "Workspace role assignment",
    privileges: "Privileges",
    privilegeSet: "Workspace role",
  },
});

export type IamOption = { value: string; label: string };
export const iamShowOptions = (mode: "all" | "item"): IamOption[] => {
  const target =
    mode === "item"
      ? TargetNodeTypes
      : mode === "all"
        ? MetaTargetNodeTypes
        : assertNever(mode);
  const { all } = ProviderNodeTitles;
  const label = (t: TargetNodeType) =>
    t in all ? all[t as keyof typeof all] : t;
  return target.map((t) => ({
    value: t,
    label: capitalize(pluralize(label(t))),
  }));
};

export const itemOnlyTypes: Readonly<(keyof AssessmentNodes)[]> = [
  "consumer",
  "resource",
];

export const identityTypeToLabelSuffix: Record<IdentityType, string> = {
  "aws-iam-role": "AWS IAM role",
  "aws-permission-set-role": "AWS role from permission set",
  "entra-app-registration": "Entra app registration",
  domain: "Everyone in a domain",
  federated: "Federated identity",
  group: "group",
  "logged-in": "All authenticated users",
  public: "Everyone on the Internet",
  "service-account": "Service account",
  "service-principal": "Service principal",
  unknown: "(unknown principal type)",
  user: "user",
};

export const identityTypeToLabel = ({
  type,
  provider,
  parent,
}: Pick<Identity, "parent" | "provider" | "type">) => {
  if (type === "group" || type === "user") {
    const providerLabel =
      provider === "custom"
        ? capitalize(parent)
        : provider
          ? providerLabels[provider]
          : "Directory";
    return `${providerLabel} ${identityTypeToLabelSuffix[type]}`;
  }
  return identityTypeToLabelSuffix[type];
};

export const MonitorRanking: Record<MonitorPriority, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};
export const priorityLabels: Record<MonitorPriority, string> = {
  CRITICAL: "Urgent",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};
